// src/hooks/useDocuments.js - Hook for accessing customer documents
import useDocumentStore from '../stores/DocumentStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing customer documents from DocumentStore
 *
 * Uses Zustand DocumentStore for state management.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Document data and operations
 */
export const useDocuments = () => {
  const socket = useSocket();

  // Documents by customer
  const documentsByCustomer = useDocumentStore(state => state.documentsByCustomer);

  // Upload state
  const uploading = useDocumentStore(state => state.uploading);
  const uploadProgress = useDocumentStore(state => state.uploadProgress);

  // Operations
  const loadCustomerDocuments = useDocumentStore(state => state.loadCustomerDocuments);
  const addDocument = useDocumentStore(state => state.addDocument);
  const updateDocument = useDocumentStore(state => state.updateDocument);
  const deleteDocument = useDocumentStore(state => state.deleteDocument);

  // Upload state management
  const setUploading = useDocumentStore(state => state.setUploading);

  // Utility functions
  const getDocumentsForCustomer = useDocumentStore(state => state.getDocumentsForCustomer);
  const isLoadingDocumentsForCustomer = useDocumentStore(state => state.isLoadingDocumentsForCustomer);
  const getTotalDocumentsForCustomer = useDocumentStore(state => state.getTotalDocumentsForCustomer);
  const clearDocumentsForCustomer = useDocumentStore(state => state.clearDocumentsForCustomer);

  // Connection state
  const isConnected = useDocumentStore(state => state.isConnected);

  // Internal initialization state
  const _initialized = useDocumentStore(state => state._initialized);

  // Fallback initialization check
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('useDocuments: Store not initialized at app level, trying fallback');
    socket.initializeStore('DocumentStore');
  }

  return {
    // Data
    documentsByCustomer,
    uploading,
    uploadProgress,

    // Operations
    loadCustomerDocuments,
    addDocument,
    updateDocument,
    deleteDocument,

    // Upload state
    setUploading,

    // Utility functions
    getDocumentsForCustomer,
    isLoadingDocumentsForCustomer,
    getTotalDocumentsForCustomer,
    clearDocumentsForCustomer,

    // Connection state
    isConnected: socket.isAuthenticated
  };
};

export default useDocuments;
